// @ts-nocheck
// grapqhl codegen doesn't generate the correct types when using aliases in query

import { NextPage } from 'next'
import { useState, useEffect, useMemo } from 'react'
import { useLazyQuery, useMutation, DocumentNode, gql } from '@apollo/client'
import { TrashIcon } from '@heroicons/react/24/outline'
import InfiniteScroll from 'react-infinite-scroll-component'
import { FaRegHandshake } from 'react-icons/fa'
import { RiHistoryFill } from 'react-icons/ri'

import { Header } from '@/components/Header'
import { CustomError } from '@/components/CustomError'
import { MissingInformation } from '@/components/MissingInformation'
import { Table } from '@/components/Table'
import { TableCell } from '@/components/TableCell'
import { TableSkeleton } from '@/components/TableSkeleton'
import { EmptyState } from '@/components/EmptyState'
import { CancelOfferModal } from '@/components/offers/CancelOfferModal'
import { OfferHistoryModal } from '@/components/offers/OfferHistoryModal'
import { Chip } from '@/components/Chip'
import AsyncCustomField from '@/components/AsyncCustomField'
import {
  CancelOfferDocument,
  CompanyFeature,
  Customer,
  MarketplaceField,
  Offer,
  OfferStatus,
  SortOrder
} from '@graphql'
import { isInformationMissing } from '@/utils/helper'
import { useUserContext } from '@/context/UserContext'
import { useCompany } from '@/hooks/useCompany'
import { Align } from '@/constants/columns'

type OffersPage = NextPage & { auth?: boolean }

const openOfferStatuses = [
  OfferStatus.Pending,
  OfferStatus.Acknowledged,
  OfferStatus.Orphaned
]

const closedOfferStatuses = [
  OfferStatus.Accepted,
  OfferStatus.Canceled,
  OfferStatus.Expired,
  OfferStatus.Matched,
  OfferStatus.Rejected
]

const Offers: OffersPage = () => {
  const [initialLoad, setInitialLoad] = useState(true)
  const [selectedOffer, setSelectedOffer] = useState<Offer>()
  const [showCancelOfferModal, setShowCancelOfferModal] = useState(false)
  const [showOfferHistoryModal, setShowOfferHistoryModal] = useState(false)
  const [showOpenOffers, setShowOpenOffers] = useState(true)
  const [showClosedOffers, setShowClosedOffers] = useState(false)
  const [offersQuery, setOffersQuery] = useState<DocumentNode>(null)

  const [getOffers, { data, loading, error, fetchMore, refetch }] =
    useLazyQuery(gql`
      query Placeholder {
        viewer {
          id
        }
      }
    `) // Placeholder query to initialize useLazyQuery

  const [cancelOffer, { loading: cancellingOffer }] = useMutation(
    CancelOfferDocument,
    {
      onCompleted: () => {
        console.log('cancel offer completed ', data)
        refetch()
        setShowCancelOfferModal(false)
      },
      onError: e => {
        console.log('cancel offer err ', e)
      }
    }
  )
  const { user, loadingUser } = useUserContext()
  const company = useCompany()

  const offerDesktopFields = useMemo(
    () => company?.settings?.marketplace?.offerDesktopFields ?? [],
    [company]
  )

  const viewer = data?.viewer as Customer
  const offers = viewer?.offers?.edges?.map(edge => edge.node) as Offer[]
  const pageInfo = viewer?.offers?.pageInfo

  const offersTableColumns = [
    ...offerDesktopFields?.map(field => {
      return {
        name: field.label ?? '',
        align: field?.styles?.includes('number') ? Align.Right : Align.Left
      }
    }),
    { name: '', align: Align.Right }
  ]

  const handleCancelOffer = () => {
    if (selectedOffer?.id) {
      cancelOffer({
        variables: {
          offerId: selectedOffer?.id
        }
      })
    }
  }

  const buildOffersQuery = (fields: MarketplaceField[]) => {
    const query = gql`
        query GetOffers(
          $status: [OfferStatus], $sort: [OfferSort], $cursor: String
        ) {
          viewer {
            ... on Customer {
              id
              offers(status: $status, sort: $sort, first: 100, after: $cursor) {
                edges {
                  node {
                    id
                    canCancel
                    updates(first: 100) {
                      edges {
                        node {
                          id
                          createdAt
                          status
                          user {
                            fullName
                          }
                        }
                      }
                    }
                    ${fields?.map(field => field.query).join('\n')}
                  }
                }
                pageInfo {
                  hasNextPage
                  endCursor
                }
              }
            }
          }
        }
      `
    return query
  }

  const getOfferStatus = () => {
    let status = [...openOfferStatuses, ...closedOfferStatuses]

    if (showOpenOffers && !showClosedOffers) {
      status = openOfferStatuses
    } else if (showClosedOffers && !showOpenOffers) {
      status = closedOfferStatuses
    }

    return status
  }

  useEffect(() => {
    if (offerDesktopFields) {
      const query = buildOffersQuery(offerDesktopFields)
      setOffersQuery(query)
    }
  }, [offerDesktopFields])

  useEffect(() => {
    if (offersQuery) {
      const status = getOfferStatus()
      getOffers({
        query: offersQuery,
        variables: { status, sort: [{ createdAt: SortOrder.Desc }] }
      })
      setInitialLoad(false)
    }
  }, [offersQuery, showOpenOffers, showClosedOffers])

  if (error) {
    const statusCode = (error?.networkError as any)?.statusCode ?? null
    return (
      <CustomError
        code={statusCode}
        message={error?.message ?? 'Error loading offers'}
      />
    )
  }

  if (isInformationMissing(CompanyFeature.Offers, user) && !loadingUser) {
    return <MissingInformation />
  }

  return (
    <>
      <Header title="Offers" />
      <div className="px-4 pb-6 sm:px-6 lg:px-8">
        <div className="sm:flex sm:items-center">
          <div className="sm:flex-auto">
            <h1 className="text-xl font-semibold text-gray-900">Offers</h1>
          </div>
        </div>
        <div className="mt-6 flex">
          <Chip
            selected={showOpenOffers}
            onClick={() => setShowOpenOffers(!showOpenOffers)}
            classes="mr-2"
          >
            Open
          </Chip>
          <Chip
            selected={showClosedOffers}
            onClick={() => setShowClosedOffers(!showClosedOffers)}
          >
            Closed
          </Chip>
        </div>
        <div className="my-8 flex flex-col">
          <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
              {loading || initialLoad ? (
                <TableSkeleton />
              ) : offers?.length > 0 ? (
                <InfiniteScroll
                  dataLength={offers?.length}
                  style={{ overflow: 'unset' }}
                  next={() => {
                    const status = getOfferStatus()

                    fetchMore({
                      query: offersQuery,
                      variables: {
                        cursor: pageInfo?.endCursor,
                        status,
                        sort: [{ createdAt: SortOrder.Desc }]
                      }
                    })
                  }}
                  hasMore={pageInfo?.hasNextPage ?? false}
                  loader={
                    <div className="mt-5 text-center">Loading more...</div>
                  }
                >
                  <Table columns={offersTableColumns}>
                    {offers?.map((offer, i) => (
                      <tr
                        key={offer.id}
                        className={i % 2 === 0 ? undefined : 'bg-gray-50'}
                      >
                        {offerDesktopFields?.map((field, index) => {
                          return (
                            <TableCell
                              key={field.label}
                              type={index === 0 ? 'bold' : 'regular'}
                              align={
                                field?.styles?.includes('number')
                                  ? Align.Right
                                  : Align.Left
                              }
                            >
                              <AsyncCustomField field={field} data={offer} />
                            </TableCell>
                          )
                        })}
                        <TableCell>
                          <div className="flex justify-center">
                            <div className="group relative">
                              <RiHistoryFill
                                className="-ml-1 mr-4 h-5 w-5 cursor-pointer text-blue-600"
                                aria-hidden="true"
                                onClick={() => {
                                  setSelectedOffer(offer)
                                  setShowOfferHistoryModal(true)
                                }}
                              />

                              <span className="pointer-events-none absolute -left-11 -top-8 rounded-md bg-gray-700 px-2 py-1 text-white opacity-0 transition-opacity group-hover:opacity-100">
                                History
                              </span>
                            </div>

                            {offer.canCancel && (
                              <div className="group relative">
                                <TrashIcon
                                  className="-ml-1 mr-2 h-5 w-5 cursor-pointer text-red-600"
                                  aria-hidden="true"
                                  onClick={() => {
                                    setSelectedOffer(offer)
                                    setShowCancelOfferModal(true)
                                  }}
                                />
                                <span className="pointer-events-none absolute -left-11 -top-8 rounded-md bg-gray-700 px-2 py-1 text-white opacity-0 transition-opacity group-hover:opacity-100">
                                  Cancel
                                </span>
                              </div>
                            )}
                          </div>
                        </TableCell>
                      </tr>
                    ))}
                  </Table>
                </InfiniteScroll>
              ) : (
                <EmptyState
                  text="No offers found"
                  subtext="Try adjusting your filters or clearing them."
                  icon={<FaRegHandshake className="h-full w-full" />}
                />
              )}
            </div>
          </div>
        </div>
      </div>
      <CancelOfferModal
        offerId={selectedOffer?.displayId}
        loading={cancellingOffer}
        open={showCancelOfferModal}
        onClose={() => setShowCancelOfferModal(false)}
        onCancelOffer={() => handleCancelOffer()}
      />
      <OfferHistoryModal
        offer={selectedOffer as Offer}
        open={showOfferHistoryModal}
        onClose={() => setShowOfferHistoryModal(false)}
      />
    </>
  )
}

Offers.auth = true

export default Offers
